import { parseEvent, type EventContact, type ParsedEvent } from '@aikoa/common'
import { supabase } from '~/lib/supabase'

export async function getEventsByAssetId(assetId: string): Promise<ParsedEvent[]> {
  const { data, error } = await supabase
    .from('events')
    .select(
      `*,
      event_contacts(
        contact:contacts(
          id,
          name,
          email
        )
      )`
    )
    .eq('asset_id', assetId)
    .or(`event_date.gte.${new Date().toISOString()},event_date.is.null`)
    .order('event_date', { ascending: true })

  if (error) {
    console.error('Error fetching events for asset:', error)
    return []
  }

  return data.map((event) => parseEvent(event)).filter((event) => !!event)
}

export async function getEventsByContactId(contactId: string): Promise<ParsedEvent[]> {
  const { data, error } = await supabase
    .from('event_contacts')
    .select(
      `
      event:events(
        *,
        asset:assets!inner(
          *,
          users_assets(
            id,
            relation_type,
            data,
            user_id
          ),
          address:asset_addresses(*),
          prices:asset_prices(*)
        )
      )
    `
    )
    .eq('contact_id', contactId)

  if (error) {
    console.error('Error fetching events for contact:', error)
    return []
  }

  const events = data
    .map((eventContact) => parseEvent(eventContact.event))
    .filter((event): event is NonNullable<typeof event> => event !== null)
    .sort(
      (eventA, eventB) =>
        new Date(eventA.event_date ?? new Date()).getTime() -
        new Date(eventB.event_date ?? new Date()).getTime()
    )

  return events
}

export async function getEventById(eventId: string) {
  const { data, error } = await supabase
    .from('events')
    .select(
      `*,
      asset:assets!inner(
        *,
        users_assets(
          id,
          relation_type,
          data,
          user_id
        ),
        address:asset_addresses(*),
        prices:asset_prices(*)
      ),
      event_contacts(
        contact:contacts(
          id,
          name,
          email,
          phone
        )
      )`
    )
    .eq('id', eventId)
    .single()

  if (error) {
    console.error('Error fetching event:', error)
    return null
  }

  return parseEvent(data)
}

export async function getContactsForAssetEvents(assetId: string): Promise<EventContact[]> {
  const { data, error } = await supabase
    .from('events')
    .select(
      `
      event_contacts(
        contact:contacts(
          id,
          name,
          email,
          phone
        )
      )
    `
    )
    .eq('asset_id', assetId)

  if (error) {
    console.error('Error fetching contacts for asset events:', error)
    return []
  }

  const contactsMap = new Map<string, EventContact>()

  for (const event of data) {
    for (const eventContact of event.event_contacts || []) {
      const contact = eventContact.contact
      if (contact && !contactsMap.has(contact.id)) {
        contactsMap.set(contact.id, contact)
      }
    }
  }

  return Array.from(contactsMap.values())
}
